import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classes from "./DataGridOutline.scss";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { IconButton } from "@toddle-edu/ds-web";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import _ from "lodash";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import LeftPane from "./components/LeftPane";

const TEST_ID_PREFIX = "dataGrid-outline";

const DataGridOutline = props => {
  const {
    style,
    sideNavData,
    leftPaneSelectedId,
    handleClickSideBarItem,
    showLoader,
  } = props;

  // let's manage previous state using ref as well so on the basis of that we can decide what to show after hidden state is removed
  const prevOutlineStateRef = useRef();
  const timeoutRef = useRef(null);

  const [outlineState, setOutlineState] = useState("collapsed");
  const [visible, setVisible] = useState(false);
  const { t } = useI18n(["common"]);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    const isCollapsed = outlineState === "collapsed";
    if (!isCollapsed) {
      timeoutRef.current = setTimeout(() => setVisible(true), 300);
    } else {
      setVisible(false);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [outlineState]);

  useEffect(() => {
    let outlineState = "collapsed";
    if (showLoader) return;

    if (_.isEmpty(sideNavData)) {
      outlineState = "hidden";
    } else {
      if (prevOutlineStateRef.current === "open") {
        outlineState = "open";
      }
      // do nothing as user might have collapsed it or kept it open
      prevOutlineStateRef.current = outlineState;
    }
    setOutlineState(outlineState);
  }, [sideNavData, showLoader]);

  const isCollapsed = outlineState === "collapsed";

  const toggleCollapse = useCallback(() => {
    let newState = "collapsed";
    if (isCollapsed) {
      newState = "open";
    }
    setOutlineState(newState);
    prevOutlineStateRef.current = newState;
  }, [isCollapsed]);

  const containerClass = classNames(classes.container, {
    [classes.containerCollapsed]: isCollapsed,
  });

  const iconButtonClasses = classNames(classes.iconButton, {
    [visible]: visible,
  });

  const skeletonLoaderComp = () => {
    return (
      <div className={classes.skeletonContainer}>
        <div
          className={classNames(
            classes.skeletonOutlineLabelContainer,
            "text-body-3"
          )}
        >
          {t("common:outline_label")}
        </div>
        <div className={classes.skeletonListContainer}>
          {_.times(15, index => {
            return <SkeletonLoader key={index} width="100%" height="33px" />;
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.wrapper} style={style}>
      {outlineState !== "hidden" ? (
        <div className={containerClass}>
          <div className={classes.contentContainer}>
            {!isCollapsed ? (
              showLoader ? (
                skeletonLoaderComp()
              ) : (
                <LeftPane
                  data={sideNavData}
                  activeItem={leftPaneSelectedId}
                  onItemClick={handleClickSideBarItem}
                  outlineLabel={t("common:outline_label")}
                />
              )
            ) : null}
          </div>

          <IconButton
            onClick={toggleCollapse}
            testId={TEST_ID_PREFIX}
            icon={visible ? <ChevronLeftOutlined /> : <ChevronRightOutlined />}
            variant="neutral"
            type="outlined"
            className={iconButtonClasses}
          />
        </div>
      ) : null}
    </div>
  );
};

DataGridOutline.propTypes = {
  style: PropTypes.object,
  sideNavData: PropTypes.array,
  leftPaneSelectedId: PropTypes.string,
  handleClickSideBarItem: PropTypes.func,
  showLoader: PropTypes.bool,
};

DataGridOutline.defaultProps = {
  style: {},
  showLoader: false,
};

export default DataGridOutline;
